"use client";

import { Plus } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Questions() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const faqs = [{
    value: "item-1",
    question: "Ile trwa budowa domu jednorodzinnego?",
    answer: "Czas realizacji stanu surowego zamkniętego to zazwyczaj od 4 do 6 miesięcy, w zależności od projektu i warunków gruntowych. Kompleksowa budowa pod klucz zajmuje od 10 do 14 miesięcy. Każdy harmonogram ustalamy indywidualnie przed rozpoczęciem prac.",
    spec: "CZAS — 10-14 MIESIĘCY"
  }, {
    value: "item-2",
    question: "Czy zapewniacie gwarancję na wykonane prace?",
    answer: "Tak. Na wszystkie nasze usługi budowlane udzielamy pisemnej gwarancji. Konstrukcje budowlane objęte są wieloletnią ochroną zgodnie z przepisami prawa budowlanego, a na prace wykończeniowe i brukarskie udzielamy 24-miesięcznej gwarancji jakości.",
    spec: "GWARANCJA — 24 MIESIĄCE"
  }, {
    value: "item-3",
    question: "Jak przygotować działkę pod budowę?",
    answer: "Zajmujemy się tym kompleksowo. Nasz zespół wykonuje niwelację terenu, wycinkę kolidującej roślinności oraz przygotowanie wykopów pod fundamenty i przyłącza. Zapewniamy pełną obsługę maszynową, aby działka była gotowa do startu inwestycji.",
    spec: "PRZYGOTOWANIE — KOMPLEKSOWE"
  }, {
    value: "item-4",
    question: "Co jeśli pojawią się nieprzewidziane koszty?",
    answer: "Stawiamy na pełną transparentność. Każda zmiana w projekcie lub dodatkowe prace są konsultowane z inwestorem przed ich wykonaniem. Przedstawiamy kosztorys i czekamy na akceptację, dzięki czemu unikasz niespodzianek w budżecie.",
    spec: "POLITYKA — ZERO NIESPODZIANEK"
  }, {
    value: "item-5",
    question: "Czy budujecie garaże wolnostojące?",
    answer: "Tak, specjalizujemy się w budowie garaży murowanych, zarówno przydomowych, jak i wolnostojących. Wykonujemy pełen zakres prac: od fundamentów, przez ściany i dach, aż po montaż bramy i wykończenie elewacji.",
    spec: "GARAŻE — MUROWANE"
  }, {
    value: "item-6",
    question: "Czy mogę dostarczyć własne materiały?",
    answer: "Tak, istnieje taka możliwość. W takim przypadku odpowiadamy za jakość wykonawstwa, natomiast gwarancja na materiały spoczywa na ich producencie. Zawsze doradzamy jednak wybór sprawdzonych dostawców, z którymi współpracujemy.",
    spec: "MATERIAŁY — ELASTYCZNOŚĆ"
  }, {
    value: "item-7",
    question: "Jak wygląda kwestia przyłączy mediów?",
    answer: "Wykonujemy pełne instalacje podziemne: wodno-kanalizacyjne oraz przygotowanie wykopów pod przyłącza elektryczne. Zapewniamy profesjonalne wykonawstwo zgodnie z projektem technicznym i wymogami dostawców mediów.",
    spec: "INSTALACJE — PODZIEMNE"
  }, {
    value: "item-8",
    question: "Czy pomagacie w zagospodarowaniu terenu?",
    answer: "Oczywiście. Oferujemy układanie kostki brukowej na podjazdach i parkingach, budowę ogrodzeń panelowych oraz murowanych, a także tworzenie tarasów i małej architektury ogrodowej, by dom był w pełni gotowy do zamieszkania.",
    spec: "BRUKARSTWO — KOMPLEKSOWE"
  }];
  return <section data-section-id="28" id="częste-pytania-o-koszty" className="bg-background py-24 md:py-32">
      <div className="container mx-auto px-6 lg:px-10">
        <div ref={ref} className={`grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 transition-all duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div className="lg:col-span-5">
            <div className="lg:sticky lg:top-24">
              <p className="font-default text-xs tracking-widest text-primary uppercase mb-8">
                S/02 — FAQ
              </p>
              <h2 className="font-default text-5xl md:text-6xl lg:text-[64px] leading-[0.95] font-bold uppercase text-foreground tracking-tight mb-6">
                Częste
                <br />
                Pytania
              </h2>
              <p className="font-default text-base md:text-[17px] text-muted-foreground leading-relaxed mb-12 max-w-md">
                Odpowiadamy na najczęściej zadawane pytania dotyczące naszych usług budowlanych i procesu inwestycyjnego.
              </p>

              <div className="border-t border-border pt-8">
                <div className="flex items-center gap-4 mb-5">
                  <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723616/img/kierownik_budowy_budsys.webp" alt="Kierownik budowy BudSys" loading="lazy" decoding="async" className="w-[60px] h-[60px] rounded-lg object-cover shrink-0" />
                  <div>
                    <p className="font-default text-[11px] tracking-wider text-muted-foreground uppercase mb-1">
                      DO TWOJEJ DYSPOZYCJI
                    </p>
                    <p className="font-default text-sm font-bold uppercase tracking-wide text-foreground leading-tight">
                      Zespół BudSys —
                      <br />
                      Generalny Wykonawca
                    </p>
                  </div>
                </div>
                <Link to="/ContactPage" className="inline-flex items-center gap-2 font-default text-sm font-semibold uppercase tracking-wider text-primary hover:text-primary/80 transition-colors">
                  Skontaktuj się z nami
                  <span aria-hidden="true">→</span>
                </Link>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            <Accordion type="single" collapsible defaultValue="item-1" className="w-full">
              {faqs.map((faq, i) => <AccordionItem key={faq.value} value={faq.value} data-index={i} className="border-b border-border first:border-t border-t-0">
                  <AccordionTrigger className="group py-8 hover:no-underline rounded-md [&>svg]:hidden text-left items-center">
                    <span className="font-default text-lg md:text-[19px] font-bold uppercase tracking-wide text-foreground pr-4 leading-snug">
                      {faq.question}
                    </span>
                    <Plus className="size-6 shrink-0 text-primary transition-transform duration-300 group-data-[state=open]:rotate-45" aria-hidden="true" />
                  </AccordionTrigger>
                  <AccordionContent className="pb-8 pr-10">
                    <p className="font-default text-base text-muted-foreground leading-[1.7] mb-4">
                      {faq.answer}
                    </p>
                    <p className="font-default text-[11px] tracking-widest uppercase text-primary inline-block border border-border px-2.5 py-1 rounded-md">
                      {faq.spec}
                    </p>
                  </AccordionContent>
                </AccordionItem>)}
            </Accordion>
          </div>
        </div>
      </div>
    </section>;
}